import {
  resolveLevelHookCandidates,
  resolveLevelVerseCandidates,
  type MusicSection
} from "./audioConfig";
import { playSfxRecipe, sfxGainForName, type SfxName } from "./sfx";

const SFX_MIN_INTERVAL_SEC: Partial<Record<SfxName, number>> = {
  cascade_step: 0.04,
  collapse_land: 0.03,
  tile_pickup: 0.05,
  warning_alert: 0.08
};

const SFX_SAMPLE_URLS: Partial<Record<SfxName, string>> = {
  match_clear: "/assets/sfx/match_clear.ogg",
  cascade_chain: "/assets/sfx/cascade_chain.ogg",
  cascade_step: "/assets/sfx/cascade_step.ogg",
  collapse_land: "/assets/sfx/collapse_land.ogg",
  swap_valid: "/assets/sfx/swap_valid.ogg",
  swap_reject: "/assets/sfx/swap_reject.ogg",
  tile_pickup: "/assets/sfx/tile_pickup.ogg",
  rotation: "/assets/sfx/rotation.ogg",
  bomb_explode: "/assets/sfx/bomb_3.ogg",
  bomb_4: "/assets/sfx/bomb_4.ogg",
  bomb_5: "/assets/sfx/bomb_5.ogg",
  wipe_sweep: "/assets/sfx/wipe.ogg",
  crate_break: "/assets/sfx/crate_break.ogg",
  powerup_arm: "/assets/sfx/powerup_arm.ogg",
  powerup_cast: "/assets/sfx/powerup_cast.ogg",
  transmute: "/assets/sfx/transmute.ogg",
  column_cutter: "/assets/sfx/column_cutter.ogg",
  row_ripper: "/assets/sfx/row_ripper.ogg",
  level_up: "/assets/sfx/level_up.ogg",
  new_color: "/assets/sfx/new_color.ogg",
  score_milestone: "/assets/sfx/score_milestone.ogg",
  score_tick: "/assets/sfx/score_tick.ogg",
  new_highscore: "/assets/sfx/new_highscore.ogg",
  groove_milestone: "/assets/sfx/groove_milestone.ogg",
  extra_life: "/assets/sfx/extra_life.ogg",
  life_lost: "/assets/sfx/life_lost.ogg",
  game_over: "/assets/sfx/gameover.ogg",
  warning_alert: "/assets/sfx/warning_alert.ogg",
  button_click: "/assets/sfx/button_click.ogg",
  menu_hover: "/assets/sfx/menu_hover.ogg",
  toggle_click: "/assets/sfx/toggle_click.ogg",
  menu_whoosh: "/assets/sfx/menu_whoosh.ogg",
  coin_gain: "/assets/sfx/coin_gain.ogg",
  buy_gems: "/assets/sfx/buy_gems.ogg",
  key_earned: "/assets/sfx/key_earned.ogg",
  shop_deny: "/assets/sfx/shop_deny.ogg",
  skin_equip: "/assets/sfx/skin_equip.ogg",
  gem_claim: "/assets/sfx/gem_claim.ogg",
  daily_treasure: "/assets/sfx/daily_treasure.ogg",
  left_rotate: "/assets/sfx/left_rotate.ogg",
  right_rotate: "/assets/sfx/right_rotate.ogg",
};

const HOOK_BOUNDARY_CATCHUP_SEC = 0.06;

type TrackCache = Record<string, AudioBuffer | null>;

interface MusicRuntimeEvent {
  kind: "HOOK_LOOP_COMPLETED";
  level: number;
  atSec: number;
}

interface ActiveMusicSource {
  source: AudioBufferSourceNode;
  gain: GainNode;
  url: string;
  level: number;
  section: MusicSection;
  loopDurationSec: number;
  startedAtSec: number;
  emittedHookLoops: number;
}

interface PendingMusicSwitch {
  source: ActiveMusicSource;
  previous: ActiveMusicSource;
  switchAtSec: number;
  intentVersion: number;
}

type AudioContextCtor = new () => AudioContext;

export interface MusicVisualFrame {
  rms: number;
  bass: number;
  treble: number;
  spectrum: number[];
  waveform: number[];
}

const MUSIC_VIS_SPECTRUM_BINS = 20;
const MUSIC_VIS_WAVEFORM_SAMPLES = 20;
const MUSIC_ANALYSER_FFT_SIZE = 256;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function averageNormalizedByteRange(values: Uint8Array, start: number, end: number): number {
  if (values.length === 0) {
    return 0;
  }
  const clampedStart = Math.max(0, Math.min(values.length, Math.floor(start)));
  const clampedEnd = Math.max(clampedStart + 1, Math.min(values.length, Math.ceil(end)));
  let sum = 0;
  for (let i = clampedStart; i < clampedEnd; i += 1) {
    sum += values[i] ?? 0;
  }
  return clamp01((sum / (clampedEnd - clampedStart)) / 255);
}

function resampleFrequency(values: Uint8Array, bins: number): number[] {
  if (bins <= 0) {
    return [];
  }
  const out: number[] = [];
  const size = Math.max(1, values.length);
  for (let i = 0; i < bins; i += 1) {
    const start = Math.floor((i / bins) * size);
    const end = Math.floor(((i + 1) / bins) * size);
    const nextEnd = Math.max(start + 1, end);
    out.push(averageNormalizedByteRange(values, start, nextEnd));
  }
  return out;
}

function resampleWaveform(values: Uint8Array, samples: number): { waveform: number[]; rms: number } {
  if (samples <= 0) {
    return { waveform: [], rms: 0 };
  }
  const sourceSize = Math.max(1, values.length);
  const waveform: number[] = [];
  let sqSum = 0;
  let sqCount = 0;

  for (let i = 0; i < samples; i += 1) {
    const start = Math.floor((i / samples) * sourceSize);
    const end = Math.max(start + 1, Math.floor(((i + 1) / samples) * sourceSize));
    let sum = 0;
    let count = 0;
    for (let s = start; s < end && s < sourceSize; s += 1) {
      const centered = ((values[s] ?? 128) - 128) / 128;
      sum += centered;
      sqSum += centered * centered;
      sqCount += 1;
      count += 1;
    }
    const avg = count > 0 ? sum / count : 0;
    waveform.push(clamp01(Math.abs(avg)));
  }

  const rms = sqCount > 0 ? Math.sqrt(sqSum / sqCount) : 0;
  return { waveform, rms: clamp01(rms) };
}

export class AudioManager {
  private ctx: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private lowpassFilter: BiquadFilterNode | null = null;
  private lowpassEnabled = false;
  private bassBoost: BiquadFilterNode | null = null;
  private reverbSend: GainNode | null = null;
  private reverbConvolver: ConvolverNode | null = null;
  private sfxBus: GainNode | null = null;
  private sfxCompressor: DynamicsCompressorNode | null = null;
  private musicAnalyser: AnalyserNode | null = null;
  private musicFreqData: Uint8Array<ArrayBuffer> | null = null;
  private musicWaveData: Uint8Array<ArrayBuffer> | null = null;

  private trackCache: TrackCache = {};
  private sfxSampleCache: TrackCache = {};

  private currentMusic: ActiveMusicSource | null = null;
  private pendingMusicSwitch: PendingMusicSwitch | null = null;
  private musicEvents: MusicRuntimeEvent[] = [];
  private switchInFlight = false;
  private rescheduleRequested = false;
  private intentVersion = 0;

  private lastSfxTime: Partial<Record<SfxName, number>> = {};
  private enabled = true;
  private sfxEnabled = true;
  private paused = false;
  private musicVolume = 0.35;
  private sfxVolume = 0.82;

  private intentLevel = 1;
  private intentSection: MusicSection = "VERSE";
  private lastLevel = 1;

  isEnabled(): boolean {
    return this.enabled;
  }

  isSfxEnabled(): boolean {
    return this.sfxEnabled;
  }

  // Backward-compatible getter used by startup logging.
  getTrackMode(): "adaptive" {
    return "adaptive";
  }

  // Kept for compatibility with older tests and call sites.
  setTrackMode(_mode: unknown): void {
    // no-op: adaptive mode is always on.
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  setMusicVolume(volume01: number): void {
    const clamped = clamp01(volume01);
    this.musicVolume = clamped;
    if (this.gainNode != null && this.ctx != null) {
      const now = this.ctx.currentTime;
      this.gainNode.gain.cancelScheduledValues(now);
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
      this.gainNode.gain.linearRampToValueAtTime(clamped, now + 0.06);
    }
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  setSfxVolume(volume01: number): void {
    const clamped = clamp01(volume01);
    this.sfxVolume = clamped;
    if (this.sfxBus != null && this.ctx != null) {
      const now = this.ctx.currentTime;
      this.sfxBus.gain.cancelScheduledValues(now);
      this.sfxBus.gain.setValueAtTime(this.sfxBus.gain.value, now);
      this.sfxBus.gain.linearRampToValueAtTime(clamped, now + 0.06);
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.stopMusic();
      return;
    }
    void this.scheduleSwitchIfNeeded();
  }

  toggleEnabled(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  async setPaused(paused: boolean): Promise<void> {
    this.paused = paused;
    if (this.ctx == null) {
      return;
    }
    try {
      if (paused) {
        if (this.ctx.state === "running") {
          await this.ctx.suspend();
        }
        return;
      }
      if (this.enabled && this.ctx.state === "suspended") {
        await this.ctx.resume();
      }
      void this.scheduleSwitchIfNeeded();
    } catch {
      // no-op
    }
  }

  async ensureContext(): Promise<void> {
    if (this.ctx != null) {
      if (!this.paused && this.ctx.state === "suspended" && this.enabled) {
        await this.ctx.resume();
      }
      return;
    }

    const constructors: AudioContextCtor[] = [];
    if (typeof window.AudioContext === "function") {
      constructors.push(window.AudioContext as AudioContextCtor);
    }
    const webkitCtor = (window as typeof window & { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (typeof webkitCtor === "function" && webkitCtor !== constructors[0]) {
      constructors.push(webkitCtor);
    }
    if (constructors.length === 0) {
      return;
    }

    for (const Ctx of constructors) {
      try {
        this.ctx = new Ctx();
        break;
      } catch {
        this.ctx = null;
      }
    }
    if (this.ctx == null) {
      return;
    }

    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = this.musicVolume;

    this.bassBoost = this.ctx.createBiquadFilter();
    this.bassBoost.type = "lowshelf";
    this.bassBoost.frequency.value = 200;
    this.bassBoost.gain.value = 0;

    this.lowpassFilter = this.ctx.createBiquadFilter();
    this.lowpassFilter.type = "lowpass";
    this.lowpassFilter.frequency.value = this.lowpassEnabled ? 400 : 22050;
    this.lowpassFilter.Q.value = 0.7;

    const reverbLen = 2.5 * this.ctx.sampleRate;
    const reverbBuf = this.ctx.createBuffer(2, reverbLen, this.ctx.sampleRate);
    for (let ch = 0; ch < 2; ch += 1) {
      const d = reverbBuf.getChannelData(ch);
      for (let i = 0; i < reverbLen; i += 1) {
        d[i] = (Math.random() * 2 - 1) * Math.exp(-3.2 * i / reverbLen);
      }
    }
    this.reverbConvolver = this.ctx.createConvolver();
    this.reverbConvolver.buffer = reverbBuf;
    this.reverbSend = this.ctx.createGain();
    this.reverbSend.gain.value = 0;

    this.gainNode.connect(this.bassBoost);
    this.bassBoost.connect(this.lowpassFilter);
    this.bassBoost.connect(this.reverbSend);
    this.reverbSend.connect(this.reverbConvolver);
    this.reverbConvolver.connect(this.lowpassFilter);
    this.musicAnalyser = this.ctx.createAnalyser();
    this.musicAnalyser.fftSize = MUSIC_ANALYSER_FFT_SIZE;
    this.musicAnalyser.smoothingTimeConstant = 0.75;
    this.musicFreqData = new Uint8Array(this.musicAnalyser.frequencyBinCount);
    this.musicWaveData = new Uint8Array(this.musicAnalyser.fftSize);
    this.lowpassFilter.connect(this.musicAnalyser);
    this.musicAnalyser.connect(this.ctx.destination);

    this.sfxBus = this.ctx.createGain();
    this.sfxBus.gain.value = this.sfxVolume;
    this.sfxCompressor = this.ctx.createDynamicsCompressor();
    this.sfxCompressor.threshold.value = -24;
    this.sfxCompressor.knee.value = 18;
    this.sfxCompressor.ratio.value = 4;
    this.sfxCompressor.attack.value = 0.006;
    this.sfxCompressor.release.value = 0.08;
    this.sfxBus.connect(this.sfxCompressor);
    this.sfxCompressor.connect(this.ctx.destination);

    if (this.paused && this.ctx.state === "running") {
      await this.ctx.suspend();
    }
  }

  setMusicIntent(level: number, section: MusicSection): void {
    const clampedLevel = Math.max(1, Math.min(20, Math.floor(level)));
    this.intentLevel = clampedLevel;
    this.intentSection = section;
    this.lastLevel = clampedLevel;
    this.intentVersion += 1;
    void this.preloadLevel(clampedLevel);
    if (clampedLevel < 20) {
      void this.preloadLevel(clampedLevel + 1);
    }
    void this.scheduleSwitchIfNeeded();
  }

  async preloadLevel(level: number): Promise<void> {
    const clampedLevel = Math.max(1, Math.min(20, Math.floor(level)));
    const verseCandidates = resolveLevelVerseCandidates(clampedLevel);
    const hookCandidates = resolveLevelHookCandidates(clampedLevel);
    for (const url of [...verseCandidates, ...hookCandidates]) {
      await this.loadTrack(url);
    }
  }

  consumeMusicEvents(): Array<{ kind: "HOOK_LOOP_COMPLETED"; level: number; atSec: number }> {
    this.pumpMusicClock();
    const out = [...this.musicEvents];
    this.musicEvents.length = 0;
    return out;
  }

  getHookLoopDurationSec(level: number): number | null {
    const clampedLevel = Math.max(1, Math.min(20, Math.floor(level)));
    const candidates = resolveLevelHookCandidates(clampedLevel);
    for (const url of candidates) {
      const buffer = this.trackCache[url];
      if (buffer == null) {
        continue;
      }
      const duration = this.loopDurationSec(buffer);
      if (duration > 0) {
        return duration;
      }
    }
    return null;
  }

  getSecondsUntilHookLoopComplete(level: number): number | null {
    this.pumpMusicClock();
    if (this.ctx == null) {
      return null;
    }

    const clampedLevel = Math.max(1, Math.min(20, Math.floor(level)));
    const now = this.ctx.currentTime;
    const currentHook = this.currentMusic?.section === "HOOK" && this.currentMusic.level === clampedLevel
      ? this.currentMusic
      : null;
    const pendingHook = this.pendingMusicSwitch?.source.section === "HOOK"
      && this.pendingMusicSwitch.source.level === clampedLevel
      ? this.pendingMusicSwitch
      : null;
    const hookLoopSec = currentHook?.loopDurationSec
      ?? pendingHook?.source.loopDurationSec
      ?? this.getHookLoopDurationSec(clampedLevel);

    if (currentHook != null) {
      const hookCompleteAtSec = this.pendingMusicSwitch?.previous === currentHook
        ? this.pendingMusicSwitch.switchAtSec
        : this.nextBoundaryTime(currentHook, now);
      return Math.max(0, hookCompleteAtSec - now);
    }

    if (pendingHook != null && hookLoopSec != null) {
      return Math.max(0, pendingHook.switchAtSec - now) + hookLoopSec;
    }

    if (
      hookLoopSec != null
      && this.intentSection === "HOOK"
      && this.intentLevel === clampedLevel
      && this.currentMusic != null
    ) {
      const hookStartAtSec = this.nextSwitchTime(this.currentMusic, now, "HOOK");
      return Math.max(0, hookStartAtSec - now) + hookLoopSec;
    }

    return null;
  }

  getSecondsToNextMusicBoundary(): number | null {
    this.pumpMusicClock();
    if (this.ctx == null) {
      return null;
    }
    const now = this.ctx.currentTime;
    if (this.pendingMusicSwitch != null) {
      return Math.max(0, this.pendingMusicSwitch.switchAtSec - now);
    }
    if (this.currentMusic == null) {
      return null;
    }
    const nextBoundary = this.nextBoundaryTime(this.currentMusic, now);
    return Math.max(0, nextBoundary - now);
  }

  getMusicVisualFrame(): MusicVisualFrame | null {
    if (
      !this.enabled ||
      this.ctx == null ||
      this.currentMusic == null ||
      this.musicAnalyser == null ||
      this.musicFreqData == null ||
      this.musicWaveData == null
    ) {
      return null;
    }
    this.pumpMusicClock();
    this.musicAnalyser.getByteFrequencyData(this.musicFreqData);
    this.musicAnalyser.getByteTimeDomainData(this.musicWaveData);

    const bassEnd = Math.max(1, Math.floor(this.musicFreqData.length * 0.18));
    const trebleStart = Math.max(0, Math.floor(this.musicFreqData.length * 0.58));
    const spectrum = resampleFrequency(this.musicFreqData, MUSIC_VIS_SPECTRUM_BINS);
    const waveformResult = resampleWaveform(this.musicWaveData, MUSIC_VIS_WAVEFORM_SAMPLES);
    return {
      rms: waveformResult.rms,
      bass: averageNormalizedByteRange(this.musicFreqData, 0, bassEnd),
      treble: averageNormalizedByteRange(this.musicFreqData, trebleStart, this.musicFreqData.length),
      spectrum,
      waveform: waveformResult.waveform
    };
  }

  async playLevelTrack(level: number): Promise<void> {
    this.setMusicIntent(level, "VERSE");
  }

  async playGrooveModeTrack(): Promise<void> {
    this.setMusicIntent(this.lastLevel, "HOOK");
  }

  async exitGrooveModeTrack(): Promise<void> {
    this.setMusicIntent(this.lastLevel, "VERSE");
  }

  async playStinger(frequency = 880, durationSec = 0.12): Promise<void> {
    await this.ensureContext();
    if (!this.enabled || this.ctx == null || this.gainNode == null) {
      return;
    }
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "square";
    osc.frequency.value = frequency;
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(this.gainNode);
    const now = this.ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationSec);
    osc.start(now);
    osc.stop(now + durationSec + 0.02);
  }

  setLowpass(enabled: boolean): void {
    this.lowpassEnabled = enabled;
    if (this.lowpassFilter != null && this.ctx != null) {
      const target = enabled ? 400 : 22050;
      this.lowpassFilter.frequency.cancelScheduledValues(this.ctx.currentTime);
      this.lowpassFilter.frequency.setValueAtTime(this.lowpassFilter.frequency.value, this.ctx.currentTime);
      this.lowpassFilter.frequency.exponentialRampToValueAtTime(target, this.ctx.currentTime + 0.3);
    }
  }

  setGrooveBuild(fill: number): void {
    if (this.ctx == null) {
      return;
    }
    const t = this.ctx.currentTime;
    if (this.reverbSend != null) {
      const wet = fill * fill * 0.45;
      this.reverbSend.gain.cancelScheduledValues(t);
      this.reverbSend.gain.setValueAtTime(this.reverbSend.gain.value, t);
      this.reverbSend.gain.linearRampToValueAtTime(wet, t + 0.12);
    }
  }

  setGrooveBassBoost(active: boolean): void {
    if (this.bassBoost == null || this.ctx == null) {
      return;
    }
    const t = this.ctx.currentTime;
    const target = active ? 10 : 0;
    this.bassBoost.gain.cancelScheduledValues(t);
    this.bassBoost.gain.setValueAtTime(this.bassBoost.gain.value, t);
    this.bassBoost.gain.linearRampToValueAtTime(target, t + (active ? 0.06 : 0.3));
  }

  setSfxEnabled(enabled: boolean): void {
    this.sfxEnabled = enabled;
  }

  async playSfx(name: SfxName, options?: { pitchMultiplier?: number }): Promise<void> {
    await this.ensureContext();
    if (!this.sfxEnabled || this.ctx == null) {
      return;
    }
    const now = this.ctx.currentTime;
    const cooldown = SFX_MIN_INTERVAL_SEC[name] ?? 0;
    const last = this.lastSfxTime[name] ?? -Infinity;
    if (now - last < cooldown) {
      return;
    }
    this.lastSfxTime[name] = now;

    const pitchMultiplier = options?.pitchMultiplier ?? 1;
    const usedSample = await this.playSampleSfx(name, pitchMultiplier);
    if (usedSample) {
      return;
    }

    const sfxGain = this.ctx.createGain();
    sfxGain.gain.value = sfxGainForName(name);
    sfxGain.connect(this.sfxBus ?? this.ctx.destination);
    playSfxRecipe(this.ctx, sfxGain, name, pitchMultiplier);
  }

  private pumpMusicClock(): void {
    if (this.ctx == null) {
      return;
    }

    this.finalizePendingSwitchIfDue();

    if (this.currentMusic != null) {
      this.emitHookLoopEvents(this.currentMusic);
    }

    if (!this.enabled || this.currentMusic == null || this.pendingMusicSwitch != null) {
      return;
    }

    if (
      this.currentMusic.level !== this.intentLevel
      || this.currentMusic.section !== this.intentSection
    ) {
      const now = this.ctx.currentTime;
      const nextBoundary = this.nextBoundaryTime(this.currentMusic, now);
      if (now >= nextBoundary - 0.02) {
        void this.scheduleSwitchIfNeeded();
      }
    }
  }

  private finalizePendingSwitchIfDue(): void {
    if (this.pendingMusicSwitch == null || this.ctx == null) {
      return;
    }
    if (this.pendingMusicSwitch.intentVersion !== this.intentVersion) {
      this.clearPendingMusicSwitch();
      return;
    }
    if (this.ctx.currentTime + 0.0005 < this.pendingMusicSwitch.switchAtSec) {
      return;
    }
    this.stopAndDisconnectSource(this.pendingMusicSwitch.previous, this.pendingMusicSwitch.switchAtSec);
    this.currentMusic = this.pendingMusicSwitch.source;
    this.pendingMusicSwitch = null;
  }

  private emitHookLoopEvents(source: ActiveMusicSource): void {
    if (source.section !== "HOOK" || this.ctx == null) {
      return;
    }
    const now = this.ctx.currentTime;
    if (now <= source.startedAtSec || source.loopDurationSec <= 0) {
      return;
    }
    const loopsElapsed = Math.floor((now - source.startedAtSec) / source.loopDurationSec);
    while (source.emittedHookLoops < loopsElapsed) {
      source.emittedHookLoops += 1;
      this.musicEvents.push({
        kind: "HOOK_LOOP_COMPLETED",
        level: source.level,
        atSec: source.startedAtSec + source.emittedHookLoops * source.loopDurationSec
      });
    }
  }

  private async scheduleSwitchIfNeeded(): Promise<void> {
    if (this.switchInFlight) {
      this.rescheduleRequested = true;
      return;
    }
    this.switchInFlight = true;

    const targetLevel = this.intentLevel;
    const targetSection = this.intentSection;
    const targetIntentVersion = this.intentVersion;

    try {
      if (!this.enabled) {
        return;
      }
      await this.ensureContext();
      if (!this.enabled || this.ctx == null || this.gainNode == null) {
        return;
      }

      const targetResolved = await this.resolveLoopBuffer(targetLevel, targetSection);
      if (
        !this.enabled
        || this.ctx == null
        || this.gainNode == null
        || targetResolved == null
        || this.intentVersion !== targetIntentVersion
        || this.intentLevel !== targetLevel
        || this.intentSection !== targetSection
      ) {
        return;
      }

      this.pumpMusicClock();

      const now = this.ctx.currentTime;
      if (this.currentMusic == null) {
        this.currentMusic = this.createSourceInstance(
          targetResolved.url,
          targetResolved.buffer,
          targetLevel,
          targetSection,
          now,
          1
        );
        return;
      }

      if (
        this.currentMusic.level === targetLevel
        && this.currentMusic.section === targetSection
        && this.pendingMusicSwitch == null
      ) {
        return;
      }

      if (
        this.pendingMusicSwitch != null
        && this.pendingMusicSwitch.source.level === targetLevel
        && this.pendingMusicSwitch.source.section === targetSection
      ) {
        return;
      }

      this.clearPendingMusicSwitch();

      const switchAtSec = this.nextSwitchTime(this.currentMusic, now, targetSection);
      const nextSource = this.createSourceInstance(
        targetResolved.url,
        targetResolved.buffer,
        targetLevel,
        targetSection,
        switchAtSec,
        1
      );

      try {
        this.currentMusic.source.stop(switchAtSec);
      } catch {
        // no-op
      }

      this.pendingMusicSwitch = {
        source: nextSource,
        previous: this.currentMusic,
        switchAtSec,
        intentVersion: targetIntentVersion
      };
      this.finalizePendingSwitchIfDue();
    } finally {
      this.switchInFlight = false;
      if (this.rescheduleRequested) {
        this.rescheduleRequested = false;
        void this.scheduleSwitchIfNeeded();
      }
    }
  }

  private createSourceInstance(
    url: string,
    buffer: AudioBuffer,
    level: number,
    section: MusicSection,
    startAtSec: number,
    initialGain: number
  ): ActiveMusicSource {
    if (this.ctx == null || this.gainNode == null) {
      throw new Error("Audio context not initialized");
    }
    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gain = this.ctx.createGain();
    gain.gain.value = initialGain;

    source.connect(gain);
    gain.connect(this.gainNode);
    source.start(startAtSec);

    return {
      source,
      gain,
      url,
      level,
      section,
      loopDurationSec: this.loopDurationSec(buffer),
      startedAtSec: startAtSec,
      emittedHookLoops: 0
    };
  }

  private stopMusic(): void {
    this.clearPendingMusicSwitch();
    this.stopAndDisconnectSource(this.currentMusic, undefined);

    this.currentMusic = null;
    this.musicEvents.length = 0;
  }

  private stopAndDisconnectSource(entry: ActiveMusicSource | null, stopAtSec?: number): void {
    if (entry == null) {
      return;
    }
    try {
      if (stopAtSec != null) {
        entry.source.stop(stopAtSec);
      } else {
        entry.source.stop();
      }
    } catch {
      // no-op
    }
    try {
      entry.source.disconnect();
    } catch {
      // no-op
    }
  }

  private clearPendingMusicSwitch(): void {
    if (this.pendingMusicSwitch == null) {
      return;
    }
    this.stopAndDisconnectSource(this.pendingMusicSwitch.source, undefined);
    this.pendingMusicSwitch = null;
  }

  private switchStepSecForSource(source: ActiveMusicSource): number {
    const loopSec = Math.max(0.01, source.loopDurationSec);
    if (source.section === "VERSE") {
      return Math.max(0.01, loopSec * 0.5);
    }
    return loopSec;
  }

  private nextBoundaryTime(source: ActiveMusicSource, nowSec: number): number {
    const stepSec = this.switchStepSecForSource(source);
    if (nowSec <= source.startedAtSec) {
      return source.startedAtSec + stepSec;
    }
    const elapsed = nowSec - source.startedAtSec;
    const stepsElapsed = Math.floor(elapsed / stepSec);
    return source.startedAtSec + (stepsElapsed + 1) * stepSec;
  }

  private nextSwitchTime(source: ActiveMusicSource, nowSec: number, targetSection: MusicSection): number {
    if (source.section !== "HOOK" || targetSection !== "VERSE") {
      return this.nextBoundaryTime(source, nowSec);
    }

    const stepSec = this.switchStepSecForSource(source);
    if (nowSec <= source.startedAtSec) {
      return source.startedAtSec + stepSec;
    }

    const elapsed = nowSec - source.startedAtSec;
    const stepsElapsed = Math.floor(elapsed / stepSec);
    const boundaryAtOrBeforeNow = source.startedAtSec + stepsElapsed * stepSec;
    const sinceBoundary = nowSec - boundaryAtOrBeforeNow;
    if (sinceBoundary >= 0 && sinceBoundary <= HOOK_BOUNDARY_CATCHUP_SEC) {
      return nowSec;
    }

    return source.startedAtSec + (stepsElapsed + 1) * stepSec;
  }

  private loopDurationSec(buffer: AudioBuffer): number {
    const duration = (buffer as AudioBuffer & { duration?: number }).duration;
    if (typeof duration !== "number" || !Number.isFinite(duration) || duration <= 0) {
      return 1;
    }
    return duration;
  }

  private async resolveLoopBuffer(
    level: number,
    section: MusicSection
  ): Promise<{ url: string; buffer: AudioBuffer } | null> {
    const candidates = section === "HOOK"
      ? resolveLevelHookCandidates(level)
      : resolveLevelVerseCandidates(level);

    for (const url of candidates) {
      const buffer = await this.loadTrack(url);
      if (buffer != null) {
        return { url, buffer };
      }
    }
    return null;
  }

  private async loadTrack(url: string): Promise<AudioBuffer | null> {
    if (this.trackCache[url] !== undefined) {
      return this.trackCache[url];
    }
    await this.ensureContext();
    if (this.ctx == null) {
      this.trackCache[url] = null;
      return null;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.trackCache[url] = null;
        return null;
      }
      const data = await response.arrayBuffer();
      const buffer = await this.ctx.decodeAudioData(data);
      this.trackCache[url] = buffer;
      return buffer;
    } catch {
      this.trackCache[url] = null;
      return null;
    }
  }

  private async loadSfxSample(url: string): Promise<AudioBuffer | null> {
    if (this.sfxSampleCache[url] !== undefined) {
      return this.sfxSampleCache[url];
    }
    await this.ensureContext();
    if (this.ctx == null) {
      this.sfxSampleCache[url] = null;
      return null;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.sfxSampleCache[url] = null;
        return null;
      }
      const data = await response.arrayBuffer();
      const buffer = await this.ctx.decodeAudioData(data);
      this.sfxSampleCache[url] = buffer;
      return buffer;
    } catch {
      this.sfxSampleCache[url] = null;
      return null;
    }
  }

  private async playSampleSfx(name: SfxName, pitchMultiplier: number): Promise<boolean> {
    const sampleUrl = SFX_SAMPLE_URLS[name];
    if (sampleUrl == null) {
      return false;
    }
    const buffer = await this.loadSfxSample(sampleUrl);
    if (buffer == null || this.ctx == null) {
      return false;
    }
    try {
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = Math.max(0.25, pitchMultiplier);
      const gain = this.ctx.createGain();
      gain.gain.value = sfxGainForName(name);
      source.connect(gain);
      gain.connect(this.sfxBus ?? this.ctx.destination);
      source.start();
      return true;
    } catch {
      return false;
    }
  }
}
